import * as React from 'react'

import * as m from 'motion/react-m'

import {
  Button,
  Flex,
  ScrollArea,
  Skeleton,
  Tooltip,
} from '@buffer-mono/popcorn'

import { graphql, readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import { safeSlice } from '../../util/safeSlice'
import { useSuggestionChipHoverMotion } from './suggestionChipMotion'

import styles from './EngagementReplySuggestions.module.css'

import type { JSX } from 'react'

/**
 * Colocated data contract for the most-similar saved-reply chip.
 *
 * This strip only reads `content.text`, but it forwards the value to
 * `onSavedReplyClick`, which is still typed against the broad codegen
 * `SavedReply` in its (unmigrated) callers; the fragment therefore selects the
 * full set of required `SavedReply` fields so it stays assignable to that
 * callback's parameter.
 */
export const EngagementReplySuggestions_SavedReply = graphql(/* GraphQL */ `
  fragment EngagementReplySuggestions_SavedReply on SavedReply @_unmask {
    id
    organizationId
    content {
      text
    }
    createdAt
    updatedAt
  }
`)

const MotionButton = m.create(Button)

const getSuggestionChipKey = (suggestionId: string): string =>
  `suggestion:${suggestionId}`

const getSavedReplyChipKey = (savedReplyId: string): string =>
  `saved-reply:${savedReplyId}`

/**
 * A suggestion chip to render. `id` carries the caller's stable identity
 * (comments use a generated UUID, mentions use `source-index`) so the shared
 * component never has to key by bare array index.
 */
export type ReplySuggestion = {
  id: string
  text: string
}

/**
 * Skeleton loading component for a single suggestion button.
 */
const SuggestionButtonSkeleton = ({
  width = 120,
}: {
  width?: number
}): JSX.Element => (
  <Skeleton
    width={width}
    height="var(--control-size-sm)"
    rounded
    className={styles.skeletonButton}
  />
)

/**
 * Skeleton loading row shown while AI suggestions are being fetched.
 */
const AISuggestionsSkeleton = (): JSX.Element => (
  <>
    <SuggestionButtonSkeleton width={110} />
    <SuggestionButtonSkeleton width={90} />
    <SuggestionButtonSkeleton width={130} />
    <SuggestionButtonSkeleton width={95} />
  </>
)

type EngagementReplySuggestionsProps = {
  /**
   * The "Replying to @user" label node. Each surface computes (and gates) its
   * own — comments hide it for private LinkedIn accounts, mentions render it
   * when an author username exists — so the strip stays agnostic to that.
   */
  replyingToLabel: React.ReactNode
  /**
   * AI suggestion chips to render. Each carries its own stable `id` for keying.
   */
  suggestions: ReplySuggestion[]
  /**
   * While `true`, renders the AI suggestion skeleton instead of the chips.
   * Comments pass this during their lazy fetch; mentions omit it (the field is
   * already resolved).
   */
  isLoading?: boolean
  /** The saved reply most similar to the engagement text, or `null`. */
  mostSimilarSavedReply: FragmentOf<
    typeof EngagementReplySuggestions_SavedReply
  > | null
  /** Returns whether the clicked AI suggestion was inserted. */
  onSuggestionClick: (text: string) => boolean
  /** Returns whether the clicked saved reply was inserted. */
  onSavedReplyClick: (
    savedReply: FragmentOf<typeof EngagementReplySuggestions_SavedReply>,
  ) => boolean
}

/**
 * Presentational strip rendered above the reply textarea, shared by the comment
 * and mention reply-form headers: the "Replying to @user" label, a
 * most-similar saved-reply chip, and the animated AI suggestion chips (with a
 * loading skeleton). It fetches no data and tracks no events; each surface
 * feeds it from its own data source and wires its own click handling. The strip
 * keeps session-local state for consumed chips so each clicked reply disappears
 * until the reply form unmounts.
 *
 * When there are no chips and nothing is loading it renders just the label
 * (no horizontal scroll area).
 */
export function EngagementReplySuggestions({
  replyingToLabel,
  suggestions,
  isLoading = false,
  mostSimilarSavedReply,
  onSuggestionClick,
  onSavedReplyClick,
}: EngagementReplySuggestionsProps): JSX.Element {
  const [consumedChipKeys, setConsumedChipKeys] = React.useState<
    ReadonlySet<string>
  >(() => new Set())

  const consumeChip = React.useCallback((chipKey: string): void => {
    setConsumedChipKeys((currentKeys) => {
      if (currentKeys.has(chipKey)) return currentKeys

      const nextKeys = new Set(currentKeys)
      nextKeys.add(chipKey)
      return nextKeys
    })
  }, [])

  const hoverMotion = useSuggestionChipHoverMotion()

  const handleSuggestionClick = React.useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement>,
      suggestion: ReplySuggestion,
    ): void => {
      e.preventDefault()
      if (onSuggestionClick(suggestion.text)) {
        consumeChip(getSuggestionChipKey(suggestion.id))
      }
    },
    [consumeChip, onSuggestionClick],
  )

  const handleSavedReplyClick = React.useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement>,
      savedReply: FragmentOf<typeof EngagementReplySuggestions_SavedReply>,
    ): void => {
      e.preventDefault()
      const savedReplyData = readFragment(
        EngagementReplySuggestions_SavedReply,
        savedReply,
      )
      if (onSavedReplyClick(savedReply)) {
        consumeChip(getSavedReplyChipKey(savedReplyData.id))
      }
    },
    [consumeChip, onSavedReplyClick],
  )

  const mostSimilarSavedReplyData = readFragment(
    EngagementReplySuggestions_SavedReply,
    mostSimilarSavedReply,
  )

  const visibleSuggestions = suggestions.filter(
    (suggestion) => !consumedChipKeys.has(getSuggestionChipKey(suggestion.id)),
  )
  const shouldShowSavedReply =
    mostSimilarSavedReply !== null &&
    mostSimilarSavedReplyData !== null &&
    !consumedChipKeys.has(getSavedReplyChipKey(mostSimilarSavedReplyData.id))
  const hasChips = visibleSuggestions.length > 0 || shouldShowSavedReply

  // No chips to show and nothing loading — render just the "Replying to @user"
  // label without the horizontal scroll area.
  if (!hasChips && !isLoading) {
    return (
      <Flex
        direction="row"
        gap="2xs"
        wrap="nowrap"
        className={styles.flexContainer}
      >
        {replyingToLabel}
      </Flex>
    )
  }

  return (
    <ScrollArea
      scrollbars="horizontal"
      visibility="hover"
      gradientFade={50}
      size="small"
      aria-label="Suggestion buttons"
      className={styles.scrollArea}
    >
      <Flex
        direction="row"
        gap="2xs"
        wrap="nowrap"
        className={styles.flexContainer}
      >
        {replyingToLabel}
        {shouldShowSavedReply && mostSimilarSavedReply && (
          <Tooltip content={mostSimilarSavedReplyData.content.text}>
            <Button
              variant="tertiary"
              size="small"
              className={styles.closestSavedReply}
              onClick={(e: React.MouseEvent<HTMLButtonElement>): void =>
                handleSavedReplyClick(e, mostSimilarSavedReply)
              }
            >
              {safeSlice(mostSimilarSavedReplyData.content.text, 25)}
            </Button>
          </Tooltip>
        )}
        {isLoading ? (
          <AISuggestionsSkeleton />
        ) : (
          visibleSuggestions.map((suggestion) => (
            <Tooltip
              key={suggestion.id}
              content={`Insert quick reply: '${suggestion.text}'`}
            >
              <MotionButton
                variant="tertiary"
                size="small"
                className={styles.suggestionButton}
                {...hoverMotion}
                onClick={(e: React.MouseEvent<HTMLButtonElement>): void =>
                  handleSuggestionClick(e, suggestion)
                }
              >
                {safeSlice(suggestion.text, 25)}
              </MotionButton>
            </Tooltip>
          ))
        )}
      </Flex>
    </ScrollArea>
  )
}

EngagementReplySuggestions.displayName = 'EngagementReplySuggestions'
